import React, { useCallback, useEffect, useRef, useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import classNames from 'classnames';
import { injectIntl } from '../../util/reactIntl';
import { types as sdkTypes } from '../../util/sdkLoader';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/ui.duck';
import { ensureListing, ensureUser } from '../../util/data';
import { formatMoney } from '../../util/currency';
import { cleanListingTitle, decodeHtmlEntities, formatSize } from '../../util/string';
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';
import useHasVerticalOverflow from '../../hooks/useHasVerticalOverflow';
import useRevealOnReady from '../../hooks/useRevealOnReady';

import { StaticPage } from '../PageBuilder/PageBuilder';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import ProductImageSlider from './ProductImageSlider';
import NotFoundPage from '../NotFoundPage/NotFoundPage';
import { NamedLink, OrderPanel } from '../../components';
import MarketplaceCard from '../../components/MarketplaceCard/MarketplaceCard';
import { handleSubmit } from '../ListingPage/ListingPage.shared';
import { richTextParagraphs } from '../../util/richText';
import { fetchReviews, getReviews } from '../ReviewsPage/ReviewsPage.duck';
import { fetchSoldItems } from '../ResalePage/ResalePage.duck';

import {
  fetchRelatedListings,
  fetchTransactionLineItems,
  getRelatedListings,
} from './ProductPage.duck';
import { setInitialValues } from '../ListingPage/ListingPage.duck';

import css from './ProductPage.module.css';
import revealCss from '../../styles/contentReveal.module.css';

// Import product images (fallback)
import image5 from './images/image-5.png';
import image6 from './images/image-6.png';
import image7 from './images/image-7.png';
import image8 from './images/image-8.png';
import image9 from './images/image-9.png';
import image10 from './images/image-10.png';
import soldProduct1 from '../ResalePage/images/sold-product1.png';
import soldProduct2 from '../ResalePage/images/sold-product2.png';
import soldProduct3 from '../ResalePage/images/sold-product3.png';
import soldProduct4 from '../ResalePage/images/sold-product4.png';

const { UUID } = sdkTypes;
const MIN_LENGTH_FOR_LONG_WORDS = 20;
const RESALE_SEARCH = 'pub_categoryLevel1=all&pub_listingType=sell-used-products';
const ROW_PREVIEW_LIMIT = 5;
const SOLD_ITEMS_LIMIT = ROW_PREVIEW_LIMIT;

const staticSoldItems = [
  {
    id: 'static-sold-1',
    imageUrl: soldProduct1,
    title: 'Mario Valentino Juliette Shoulder Bag in Cognac',
    brandName: 'VALENTINO',
    price: { amount: 17500, currency: 'USD' },
  },
  {
    id: 'static-sold-2',
    imageUrl: soldProduct2,
    title: 'Blue and Brown Suede Round-Toe Pump',
    brandName: 'HERMES',
    price: { amount: 17500, currency: 'USD' },
  },
  {
    id: 'static-sold-3',
    imageUrl: soldProduct3,
    title: 'Clic H Panoplie Equestre bracelet',
    brandName: 'HERMES',
    price: { amount: 17500, currency: 'USD' },
  },
  {
    id: 'static-sold-4',
    imageUrl: soldProduct4,
    title: 'Patricia Washed Satin Wrap Red Dress',
    brandName: 'PAUL & JOE',
    price: { amount: 17500, currency: 'USD' },
  },
];

const expandSoldItemsForPreview = (items, limit) => {
  if (!Array.isArray(items) || items.length === 0) {
    return [];
  }

  return Array.from({ length: limit }, (_, index) => {
    const item = items[index % items.length];
    return {
      ...item,
      id: `${item.id || `sold-item-${index % items.length}`}-product-page-${index}`,
    };
  });
};

const capitalizeWords = text => {
  if (!text) {
    return '';
  }

  return text
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const capitalizeFirstCharacter = text => {
  if (!text) {
    return '';
  }

  return text.charAt(0).toUpperCase() + text.slice(1);
};

const ProductPageComponent = props => {
  const {
    params,
    getListing,
    intl,
    currentUser,
    onManageDisableScrolling,
    onFetchTransactionLineItems,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    onInitializeCardPaymentData,
    callSetInitialValues,
    history,
    location,
    showListingError,
    relatedListings,
    reviews,
    soldItems,
    onFetchRelatedListings,
    onFetchRelatedReviews,
    onFetchSoldItems,
  } = props;

  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const [readyListingId, setReadyListingId] = useState(null);
  const detailsColumnRef = useRef(null);
  const productDetailsRef = useRef(null);

  // Get listing data
  const listingId = new UUID(params.id);
  const listing = getListing(listingId);
  const currentListing = ensureListing(listing);
  const currentListingId = currentListing?.id?.uuid || null;
  const isListingResolved = Boolean(currentListing.id);
  const shouldShowNotFound = Boolean(showListingError && !currentListing.id);

  const { title, description, price, publicData = {} } = currentListing.attributes;

  const brandName = decodeHtmlEntities(publicData.BrandName);
  const brandSlug = publicData.brandSlug || '';
  const category = publicData.categoryLevel1 || '';
  const categoryLabel = capitalizeWords(category);
  const productTitle = cleanListingTitle(title, brandName);
  const productBreadcrumbLabel = [brandName, productTitle].filter(Boolean).join(' ');
  const categorySearch = category
    ? `pub_categoryLevel1=${encodeURIComponent(category)}&pub_listingType=sell-used-products`
    : null;

  const formattedSize = formatSize(publicData);

  const productCondition = publicData.ProductCondition || '';
  const formattedProductCondition =
    productCondition
      ?.toLowerCase()
      .split('-')
      .join(' ') || 'N/A';
  const formattedPrice = price ? formatMoney(intl, price).replace(/\.00\b/, '') : null;
  const productMetaPills = [
    formattedSize && formattedSize !== 'N/A'
      ? { label: 'Size', value: capitalizeFirstCharacter(formattedSize) }
      : null,
    formattedProductCondition && formattedProductCondition !== 'n/a'
      ? { label: 'Condition', value: capitalizeFirstCharacter(formattedProductCondition) }
      : null,
  ].filter(Boolean);
  const descriptionContent = description
    ? richTextParagraphs(
        description,
        {
          linkify: true,
          longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
          longWordClass: css.longWord,
        },
        css.descriptionParagraph
      )
    : [];

  // Get images from listing
  const images = currentListing.images || [];
  const imageUrls = images
    .map(
      img =>
        img.attributes?.variants?.['scaled-large']?.url ||
        img.attributes?.variants?.['scaled-medium']?.url
    )
    .filter(Boolean);
  const videos = currentListing.attributes.publicData.videoUrls || [];

  // Use a single placeholder when a listing has no usable media to avoid flashing a fake thumbnail rail.
  const displayImages =
    imageUrls.length > 0 || videos.length > 0 ? [...imageUrls, ...videos] : [image5];
  const relatedListingsToRender = Array.isArray(relatedListings)
    ? relatedListings
        .filter(item => item?.attributes?.state !== 'closed')
        .filter(item => item?.id?.uuid !== currentListing.id.uuid)
        .slice(0, ROW_PREVIEW_LIMIT)
    : [];
  const relatedReviewsToRender = Array.isArray(reviews) ? reviews.slice(0, ROW_PREVIEW_LIMIT) : [];
  const soldItemsToRender = expandSoldItemsForPreview(
    soldItems.length > 0 ? soldItems : staticSoldItems,
    SOLD_ITEMS_LIMIT
  );
  const hasDetailOverflow = useHasVerticalOverflow(detailsColumnRef, productDetailsRef, [
    currentListingId,
    description,
    formattedPrice,
    productMetaPills.length,
    productTitle,
    soldItemsToRender.length,
  ]);

  const schemaTitle = title;
  const schemaDescription = description || 'Shop authentic pre-loved fashion items with confidence';
  const isProductMediaReady = Boolean(currentListingId && readyListingId === currentListingId);
  const hasRevealedContent = useRevealOnReady(isProductMediaReady, currentListingId);
  const revealedContentClassName = classNames(revealCss.reveal, {
    [revealCss.hidden]: !hasRevealedContent,
    [revealCss.visible]: hasRevealedContent,
  });

  // Check if user is owner
  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;

  const currentAuthor = authorAvailable ? currentListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);

  const handleOrderSubmit = values => {
    const isCurrentlyClosed = currentListing.attributes.state === 'closed';
    if (isOwnListing || isCurrentlyClosed) {
      window.scrollTo(0, 0);
    } else {
      const commonParams = {
        params: { id: currentListing.id.uuid },
        history,
        routes: routeConfiguration,
      };
      handleSubmit({
        ...commonParams,
        currentUser,
        callSetInitialValues,
        getListing,
        onInitializeCardPaymentData,
      })(values);
    }
  };

  const ogImage = images.length > 0 ? [images[0].attributes.variants['scaled-large']] : [];

  const handleProductMediaReady = useCallback(() => {
    if (currentListingId) {
      setReadyListingId(currentListingId);
    }
  }, [currentListingId]);

  useEffect(() => {
    if (!currentListingId) {
      setReadyListingId(null);
      return;
    }

    setReadyListingId(previousReadyListingId =>
      previousReadyListingId === currentListingId ? previousReadyListingId : null
    );
  }, [currentListingId]);

  useEffect(() => {
    const listingType = currentListing?.attributes?.publicData?.listingType;
    if (listingType === 'review') {
      window.location.replace(`/reviews/${params.slug}/${currentListing.id.uuid}`); // 301 redirect
    }
  }, [currentListing]);

  useEffect(() => {
    onFetchRelatedListings({
      brandSlug,
      category,
      currentListingId: currentListing?.id?.uuid,
    });
    onFetchRelatedReviews({ brandSlug, category });
    onFetchSoldItems();
  }, [
    brandSlug,
    category,
    currentListing?.id?.uuid,
    onFetchRelatedListings,
    onFetchRelatedReviews,
    onFetchSoldItems,
  ]);

  if (!isListingResolved && !shouldShowNotFound) {
    return null;
  }

  if (shouldShowNotFound) {
    return <NotFoundPage />;
  }

  return (
    <StaticPage
      title={schemaTitle}
      description={schemaDescription}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        description: schemaDescription,
        name: schemaTitle,
      }}
      socialSharing={{ images1200: ogImage }}
    >
      {!isProductMediaReady ? <div className={css.pageMask} aria-hidden="true" /> : null}
      <TopbarContainer />
      <div className={classNames(css.root, revealedContentClassName)}>
        <main className={css.main}>
          {/* Two Column Product Layout */}
          <section className={css.productSection}>
            <div className={css.productContainer}>
              <div className={css.breadcrumbs} role="navigation" aria-label="Resale breadcrumbs">
                <NamedLink
                  name="ResalePage"
                  to={{ search: RESALE_SEARCH }}
                  className={css.breadcrumbPrimaryLink}
                >
                  SHOP NOW
                </NamedLink>
                {categoryLabel ? (
                  <>
                    <span className={css.breadcrumbSeparator} aria-hidden="true">
                      /
                    </span>
                    {categorySearch ? (
                      <NamedLink
                        name="ResalePage"
                        to={{ search: categorySearch }}
                        className={css.breadcrumbLink}
                      >
                        {categoryLabel}
                      </NamedLink>
                    ) : (
                      <span className={css.breadcrumbCurrent}>{categoryLabel}</span>
                    )}
                  </>
                ) : null}
                <span className={css.breadcrumbSeparator} aria-hidden="true">
                  /
                </span>
                <span className={css.breadcrumbCurrent}>{productBreadcrumbLabel}</span>
              </div>
              {/* Two Column Grid */}
              <div className={css.twoColumnGrid}>
                {/* First Column - Image Slider */}
                <div className={css.mediaColumn}>
                  <ProductImageSlider
                    images={displayImages}
                    imageAlt={params.slug}
                    onReady={handleProductMediaReady}
                  />
                </div>
                {/* Second Column - Product Details */}
                <div
                  ref={detailsColumnRef}
                  className={`${css.detailsColumn} ${
                    hasDetailOverflow ? css.detailsColumnScrollable : ''
                  }`}
                >
                  <div ref={productDetailsRef} className={css.productDetails}>
                    <div className={css.detailsTopRow}>
                      <div className={css.leftInfoStack}>
                        <div className={css.brandTag}>
                          <span className={css.brandName}>{brandName}</span>
                        </div>
                        <h1 className={css.productTitle}>{productTitle}</h1>
                        {productMetaPills.length > 0 ? (
                          <div
                            className={css.productMetaPills}
                            aria-label="Product size and condition"
                          >
                            {productMetaPills.map(({ label, value }) => (
                              <span className={css.productMetaPill} key={label}>
                                <span className={css.productMetaPillLabel}>{`${label}:`}</span>
                                <span className={css.productMetaPillValue}>{value}</span>
                              </span>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    </div>

                    {formattedPrice ? (
                      <div className={css.productPrice}>{formattedPrice}</div>
                    ) : null}

                    <div className={css.descriptionCallout}>
                      {descriptionContent.length > 0 ? (
                        descriptionContent
                      ) : (
                        <p className={css.descriptionParagraph}>No description available.</p>
                      )}
                    </div>

                    <OrderPanel
                      className={css.orderPanel}
                      listing={currentListing}
                      isOwnListing={isOwnListing}
                      onSubmit={handleOrderSubmit}
                      title={title}
                      author={ensuredAuthor}
                      onManageDisableScrolling={onManageDisableScrolling}
                      onFetchTransactionLineItems={onFetchTransactionLineItems}
                      lineItems={lineItems}
                      fetchLineItemsInProgress={fetchLineItemsInProgress}
                      fetchLineItemsError={fetchLineItemsError}
                      validListingTypes={config.listing.listingTypes}
                      marketplaceCurrency={config.currency}
                      dayCountAvailableForBooking={config.stripe?.dayCountAvailableForBooking || 90}
                      marketplaceName={config.marketplaceName}
                    />

                    <div className={css.finePrintSection}>
                      <hr className={css.finePrintDivider} />
                      <p className={css.finePrintText}>
                        Ships via USPS within 5 business days; payment held until delivery, then
                        released to the seller. International available case by case —{' '}
                        <a className={css.finePrintLink} href="mailto:hello@unvarnished.clothing">
                          reach out
                        </a>{' '}
                        and we'll see what we can do. Doesn't fit? Relist at a reduced 10%
                        commission. Item misrepresented? We'll cancel the order and send a prepaid
                        return label.{' '}
                        <NamedLink name="StartSellingSignupPage" className={css.finePrintLink}>
                          FAQ →
                        </NamedLink>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className={css.contentRowsStack}>
                {relatedListingsToRender.length > 0 ? (
                  <section className={css.contentRowSection}>
                    <div className={css.contentRowHeader}>
                      <p className={css.contentRowEyebrow}>You may also like</p>
                      <NamedLink
                        name="ResalePage"
                        to={{ search: categorySearch || RESALE_SEARCH }}
                        className={css.contentRowViewAll}
                      >
                        VIEW ALL
                      </NamedLink>
                    </div>
                    <div className={css.horizontalCardRow}>
                      {relatedListingsToRender.map(listing => (
                        <MarketplaceCard
                          key={listing.id.uuid}
                          listing={listing}
                          variant="resale"
                          className={css.rowCard}
                        />
                      ))}
                    </div>
                  </section>
                ) : null}

                <section className={`${css.contentRowSection} ${css.middleRowSection}`}>
                  <div className={css.contentRowHeader}>
                    <p className={css.contentRowEyebrow}>Someone beat you to it</p>
                    <NamedLink
                      name="ResalePage"
                      to={{ search: categorySearch || RESALE_SEARCH }}
                      className={css.contentRowViewAll}
                    >
                      VIEW ALL
                    </NamedLink>
                  </div>
                  <div
                    className={`${css.soldItemsRailWrap} ${
                      soldItemsToRender.length <= 5 ? css.soldItemsRailWrapNoFade : ''
                    }`}
                  >
                    <div
                      className={`${css.soldItemsRail} ${
                        soldItemsToRender.length <= 5 ? css.soldItemsRailCentered : ''
                      }`}
                    >
                      {soldItemsToRender.length > 0 ? (
                        soldItemsToRender.map((item, index) => (
                          <MarketplaceCard
                            key={item.id || `product-page-sold-${index}`}
                            listing={item}
                            variant="resale-sold"
                            className={css.soldRowCard}
                          />
                        ))
                      ) : (
                        <div className={css.emptyState}>Sold items are loading…</div>
                      )}
                    </div>
                  </div>
                </section>

                <section className={css.contentRowSection}>
                  <div className={css.contentRowHeader}>
                    <p className={css.contentRowEyebrow}>The Research Archive</p>
                    <NamedLink name="ReviewsPage" className={css.contentRowViewAll}>
                      VIEW ALL
                    </NamedLink>
                  </div>
                  <div className={`${css.horizontalCardRow} ${css.reviewCardGridRow}`}>
                    {relatedReviewsToRender.length > 0 ? (
                      relatedReviewsToRender.map(listing => (
                        <MarketplaceCard
                          key={listing.id.uuid}
                          listing={listing}
                          variant="review"
                          className={`${css.rowCard} ${css.reviewRowCard}`}
                        />
                      ))
                    ) : (
                      <div className={css.emptyState}>Related reviews are loading…</div>
                    )}
                  </div>
                </section>
              </div>
            </div>
          </section>
        </main>
      </div>
      <FooterContainer />
    </StaticPage>
  );
};

const mapStateToProps = state => {
  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const { currentUser } = state.user;
  const { lineItems, fetchLineItemsInProgress, fetchLineItemsError } = state.ProductPage;

  return {
    getListing,
    currentUser,
    scrollingDisabled: isScrollingDisabled(state),
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    showListingError: state.ProductPage?.showListingError,
    relatedListings: getRelatedListings(state),
    reviews: getReviews(state),
    soldItems: state.ResalePage?.soldItems || [],
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onFetchTransactionLineItems: params => dispatch(fetchTransactionLineItems(params)),
  onFetchRelatedListings: params => dispatch(fetchRelatedListings(params)),
  onFetchRelatedReviews: ({ brandSlug, category }) => {
    const primaryParams = {
      perPage: 12,
      ...(brandSlug ? { pub_brandSlug: brandSlug } : {}),
      ...(!brandSlug && category ? { pub_categoryLevel1: category } : {}),
    };
    const fallbackParams = { perPage: 12 };
    const hasScopedFilter = Boolean(brandSlug || category);

    return dispatch(fetchReviews(primaryParams))
      .then(response => {
        const hasResults = (response?.data?.data || []).length > 0;
        if (hasResults || !hasScopedFilter) {
          return response;
        }

        return dispatch(fetchReviews(fallbackParams));
      })
      .catch(() => dispatch(fetchReviews(fallbackParams)));
  },
  onFetchSoldItems: () => dispatch(fetchSoldItems()),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
});

const ProductPage = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  injectIntl
)(ProductPageComponent);

export default ProductPage;
